import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, Filter, Calendar, MoreHorizontal,
  Clock, CheckCircle, Circle, AlertCircle, X, ChevronRight, Trash2, ArrowRight, Lock, GripVertical
} from 'lucide-react';
import {
  DndContext, DragOverlay, closestCorners, useSensor, useSensors, PointerSensor,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { getPermissions } from '../../utils/permissions';

const columns = [
  { id: 'backlog', title: 'Backlog', color: 'gray' },
  { id: 'in-progress', title: 'In Progress', color: 'blue' },
  { id: 'review', title: 'Review', color: 'yellow' },
  { id: 'completed', title: 'Completed', color: 'green' },
];
const columnOrder = ['backlog', 'in-progress', 'review', 'completed'];

const priorityColors = { high: 'bg-red-500', medium: 'bg-yellow-500', low: 'bg-green-500' };

const getColumnIcon = (color) => {
  const map = {
    gray: <Circle className="w-4 h-4 text-gray-400" />,
    blue: <AlertCircle className="w-4 h-4 text-blue-400" />,
    yellow: <Clock className="w-4 h-4 text-yellow-400" />,
    green: <CheckCircle className="w-4 h-4 text-green-400" />,
  };
  return map[color] || map.gray;
};

// ── Draggable Task Card ────────────────────────────────────

const SortableTaskCard = ({ task, onClick, onMove, canDrag }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({
    id: task.id,
    disabled: !canDrag,
    data: { type: 'task', task },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const [showMenu, setShowMenu] = useState(false);
  const currentIndex = columnOrder.indexOf(task.column);
  const nextColumn = currentIndex < columnOrder.length - 1 ? columnOrder[currentIndex + 1] : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-200 cursor-pointer group ${
        !canDrag ? 'opacity-70' : ''
      }`}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start gap-2">
        {/* Drag handle */}
        {canDrag && (
          <div
            {...attributes}
            {...listeners}
            className="mt-0.5 p-0.5 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            data-testid={`drag-handle-${task.id}`}
          >
            <GripVertical className="w-4 h-4 text-gray-500" />
          </div>
        )}

        <div className="flex-1 min-w-0" onClick={() => onClick(task)}>
          <div className="flex items-start justify-between mb-3">
            <h4 className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors pr-2">
              {task.title}
            </h4>
            <div className="relative flex-shrink-0">
              <button
                className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all"
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              >
                <MoreHorizontal className="w-4 h-4 text-gray-400" />
              </button>
              <AnimatePresence>
                {showMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute right-0 top-full mt-1 w-44 rounded-xl bg-[#0d1221] border border-white/10 shadow-2xl z-10 py-1 overflow-hidden"
                  >
                    {nextColumn && (
                      <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/10 transition-colors"
                        onClick={(e) => { e.stopPropagation(); onMove(task.id, nextColumn); setShowMenu(false); }}
                      >
                        <ArrowRight className="w-4 h-4" />
                        Move to {columns.find(c => c.id === nextColumn)?.title}
                      </button>
                    )}
                    {task.column !== 'completed' && (
                      <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-400 hover:bg-white/10 transition-colors"
                        onClick={(e) => { e.stopPropagation(); onMove(task.id, 'completed'); setShowMenu(false); }}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Mark Complete
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {task.meetingId && (
            <div className="mb-3">
              <span className="px-2 py-1 rounded-md bg-blue-500/10 text-blue-400 text-xs">AI-Generated</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${priorityColors[task.priority]}`}></div>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {task.dueDate}
              </span>
            </div>
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center" title={task.owner}>
              <span className="text-[10px] font-bold text-white">{task.ownerInitials}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Drag Overlay (ghost card while dragging) ───────────────

const DragOverlayCard = ({ task }) => {
  if (!task) return null;
  return (
    <div className="p-4 rounded-xl bg-[#161d30] border-2 border-blue-500/40 shadow-2xl shadow-blue-500/10 w-[280px] rotate-2 scale-105">
      <h4 className="text-sm font-medium text-white mb-2">{task.title}</h4>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${priorityColors[task.priority]}`}></div>
          <span className="text-xs text-gray-400">{task.dueDate}</span>
        </div>
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
          <span className="text-[10px] font-bold text-white">{task.ownerInitials}</span>
        </div>
      </div>
    </div>
  );
};

// ── Droppable Column ───────────────────────────────────────

const DroppableColumn = ({ column, tasks, children, isOver }) => {
  const { setNodeRef } = useDroppable({ id: column.id, data: { type: 'column' } });

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col rounded-2xl backdrop-blur-xl border overflow-hidden transition-colors duration-200 ${
        isOver
          ? 'bg-blue-500/10 border-blue-500/30'
          : 'bg-white/5 border-white/10'
      }`}
      data-testid={`column-${column.id}`}
    >
      {children}
    </div>
  );
};

// ── Task Detail Panel (unchanged) ──────────────────────────

const TaskDetailPanel = ({ task, onClose, onMove, onDelete, perms }) => {
  if (!task) return null;
  const currentIndex = columnOrder.indexOf(task.column);
  const nextColumn = currentIndex < columnOrder.length - 1 ? columnOrder[currentIndex + 1] : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      className="fixed right-0 top-0 h-screen w-96 bg-[#0d1221] border-l border-white/10 shadow-2xl z-50 flex flex-col"
      data-testid="task-detail-panel"
    >
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Task Details</h2>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <div>
          <h3 className="text-white font-semibold text-lg mb-2">{task.title}</h3>
          {task.meetingId && (
            <span className="px-2 py-1 rounded-md bg-blue-500/10 text-blue-400 text-xs">AI-Generated from Meeting</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Status', value: task.column, cls: task.column === 'completed' ? 'bg-green-500/20 text-green-400' : task.column === 'in-progress' ? 'bg-blue-500/20 text-blue-400' : task.column === 'review' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400' },
            { label: 'Priority', value: task.priority, cls: task.priority === 'high' ? 'bg-red-500/20 text-red-400' : task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400' },
          ].map(({ label, value, cls }) => (
            <div key={label} className="p-3 rounded-xl bg-white/5">
              <p className="text-xs text-gray-400 mb-1">{label}</p>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${cls}`}>{value}</span>
            </div>
          ))}
          <div className="p-3 rounded-xl bg-white/5">
            <p className="text-xs text-gray-400 mb-1">Owner</p>
            <p className="text-white text-sm">{task.owner}</p>
          </div>
          <div className="p-3 rounded-xl bg-white/5">
            <p className="text-xs text-gray-400 mb-1">Due Date</p>
            <p className="text-white text-sm">{task.dueDate}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {perms.canMoveTask && nextColumn && (
            <button onClick={() => onMove(task.id, nextColumn)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-500/20 text-blue-400 text-sm font-medium hover:bg-blue-500/30 transition-colors" data-testid="task-move-forward-btn">
              <ArrowRight className="w-4 h-4" />
              Move to {columns.find(c => c.id === nextColumn)?.title}
            </button>
          )}
          {perms.canMoveTask && task.column !== 'completed' && (
            <button onClick={() => onMove(task.id, 'completed')} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-500/20 text-green-400 text-sm font-medium hover:bg-green-500/30 transition-colors" data-testid="task-complete-btn">
              <CheckCircle className="w-4 h-4" />
              Mark Complete
            </button>
          )}
          {perms.canDeleteTask ? (
            <button onClick={() => { onDelete(task.id); onClose(); }} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-500/10 text-red-400 text-sm font-medium hover:bg-red-500/20 transition-colors" data-testid="task-delete-btn">
              <Trash2 className="w-4 h-4" />
              Delete Task
            </button>
          ) : (
            <div className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 text-gray-500 text-sm cursor-not-allowed" title="Insufficient permissions" data-testid="task-delete-disabled">
              <Lock className="w-4 h-4" />
              Delete (Admin only)
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ── Create Task Modal ──────────────────────────────────────

const CreateTaskModal = ({ isOpen, onClose, onCreate }) => {
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [owner, setOwner] = useState('John Doe');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!title.trim()) return;
    onCreate({
      title: title.trim(), priority, owner,
      ownerInitials: owner.split(' ').map(n => n[0]).join(''),
      dueDate: new Date(Date.now() + 7 * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    });
    setTitle('');
    setPriority('medium');
    onClose();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-md rounded-2xl bg-[#0d1221] border border-white/10 shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="create-task-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-white">Create New Task</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Task Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter task title..."
              className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50" autoFocus data-testid="create-task-title-input" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Priority</label>
            <div className="flex gap-2">
              {['low', 'medium', 'high'].map((p) => (
                <button key={p} onClick={() => setPriority(p)} data-testid={`priority-${p}-btn`}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${
                    priority === p
                      ? p === 'high' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : p === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-white/5 text-gray-400 border border-white/10'
                  }`}>{p}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Owner</label>
            <select value={owner} onChange={(e) => setOwner(e.target.value)}
              className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer">
              {['John Doe', 'Sarah Chen', 'Mike Torres', 'Jennifer Walsh', 'Robert Kim'].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors text-sm font-medium">Cancel</button>
          <button onClick={handleSubmit} disabled={!title.trim()} data-testid="create-task-submit-btn"
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">Create Task</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Main TasksScreen ───────────────────────────────────────

export const TasksScreen = () => {
  const { tasks, createTask, moveTask, deleteTask, reorderTasks } = useData();
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [overColumnId, setOverColumnId] = useState(null);

  const filteredTasks = useMemo(() =>
    tasks.filter(t => !searchQuery || t.title.toLowerCase().includes(searchQuery.toLowerCase())),
    [tasks, searchQuery]
  );

  const getColumnTasks = (colId) => filteredTasks
    .filter(t => t.column === colId)
    .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (!over) { setOverColumnId(null); return; }
    const overData = over.data?.current;
    if (overData?.type === 'column') {
      setOverColumnId(over.id);
    } else if (overData?.type === 'task') {
      setOverColumnId(overData.task.column);
    } else if (columnOrder.includes(over.id)) {
      setOverColumnId(over.id);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColumnId(null);

    if (!over || !active) return;

    const draggedTask = tasks.find(t => t.id === active.id);
    if (!draggedTask) return;

    // Determine target column and position
    let targetColumn = null;
    let overTaskId = null;
    const overData = over.data?.current;
    if (overData?.type === 'column') {
      targetColumn = over.id;
    } else if (overData?.type === 'task') {
      targetColumn = overData.task.column;
      overTaskId = overData.task.id;
    } else if (columnOrder.includes(over.id)) {
      targetColumn = over.id;
    }

    if (!targetColumn) return;

    // Same column reorder
    if (targetColumn === draggedTask.column && overTaskId && overTaskId !== draggedTask.id) {
      const colTasks = getColumnTasks(draggedTask.column);
      const oldIndex = colTasks.findIndex(t => t.id === draggedTask.id);
      const newIndex = colTasks.findIndex(t => t.id === overTaskId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      // Reorder
      const reordered = [...colTasks];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      // Update orders via backend
      const orderUpdates = reordered.map((t, i) => ({ id: t.id, order: i }));
      reorderTasks(orderUpdates);
      return;
    }

    // Different column move
    if (targetColumn !== draggedTask.column) {
      moveTask(draggedTask.id, targetColumn);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverColumnId(null);
  };

  const handleMoveTask = (taskId, newColumn) => {
    moveTask(taskId, newColumn);
    if (selectedTask?.id === taskId) {
      setSelectedTask(prev => prev ? { ...prev, column: newColumn } : null);
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="tasks-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Tasks</h1>
          <p className="text-gray-400 mt-1">Drag and drop tasks between columns to update status</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 h-10 pl-10 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50" data-testid="tasks-search-input" />
          </div>
          <button className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
            <Filter className="w-4 h-4" /><span className="text-sm">Filter</span>
          </button>
          {perms.canCreateTask ? (
            <button onClick={() => setShowCreateModal(true)} data-testid="add-task-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity">
              <Plus className="w-4 h-4" /><span className="text-sm">Add Task</span>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-gray-500 cursor-not-allowed" title="Insufficient permissions" data-testid="add-task-disabled">
              <Lock className="w-4 h-4" /><span className="text-sm">Add Task</span>
            </div>
          )}
        </div>
      </div>

      {/* Kanban Board with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex-1 grid grid-cols-4 gap-4 overflow-hidden">
          {columns.map((column) => {
            const colTasks = getColumnTasks(column.id);
            const isOver = overColumnId === column.id;

            return (
              <DroppableColumn key={column.id} column={column} tasks={colTasks} isOver={isOver}>
                {/* Column Header */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getColumnIcon(column.color)}
                      <h3 className="font-medium text-white">{column.title}</h3>
                      <span className="px-2 py-0.5 rounded-full bg-white/10 text-xs text-gray-400" data-testid={`column-count-${column.id}`}>
                        {colTasks.length}
                      </span>
                    </div>
                    {perms.canCreateTask && (
                      <button className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" onClick={() => setShowCreateModal(true)}>
                        <Plus className="w-4 h-4 text-gray-400" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Tasks area */}
                <div className={`flex-1 overflow-y-auto p-3 space-y-3 min-h-[60px] transition-colors duration-200 ${
                  isOver ? 'bg-blue-500/5' : ''
                }`}>
                  <SortableContext items={colTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                    {colTasks.map((task) => (
                      <SortableTaskCard
                        key={task.id}
                        task={task}
                        onClick={setSelectedTask}
                        onMove={handleMoveTask}
                        canDrag={perms.canMoveTask}
                      />
                    ))}
                  </SortableContext>
                  {colTasks.length === 0 && (
                    <div className={`flex items-center justify-center h-20 rounded-xl border-2 border-dashed transition-colors ${
                      isOver ? 'border-blue-500/40 text-blue-400/60' : 'border-white/5 text-gray-600'
                    }`}>
                      <p className="text-xs">{isOver ? 'Drop here' : 'No tasks'}</p>
                    </div>
                  )}
                </div>
              </DroppableColumn>
            );
          })}
        </div>

        {/* Drag Overlay */}
        <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
          {activeTask ? <DragOverlayCard task={activeTask} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Task Detail Panel */}
      <AnimatePresence>
        {selectedTask && (
          <>
            <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setSelectedTask(null)} />
            <TaskDetailPanel task={selectedTask} onClose={() => setSelectedTask(null)} onMove={handleMoveTask} onDelete={deleteTask} perms={perms} />
          </>
        )}
      </AnimatePresence>

      {/* Create Task Modal */}
      <AnimatePresence>
        {showCreateModal && <CreateTaskModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onCreate={createTask} />}
      </AnimatePresence>
    </div>
  );
};
